/**
 * DocImportModal — the app-wide home of Document → Stories (See the work).
 *
 * Mounted once in Layout so the flow is reachable from EVERY authenticated
 * page, two ways:
 *   1. The OPEN_DOC_IMPORT_EVENT window event ("+ Add data" menu, the
 *      Meeting-notes connect card, or any future surface) — optionally
 *      carrying a `detail.file` to start analyzing immediately.
 *   2. Dragging a .docx / .txt / .md anywhere over the app — a full-screen
 *      drop overlay appears ("Drop your meeting notes — Avium drafts the
 *      Stories"), and dropping opens this modal with the file pre-loaded.
 *
 * The drag overlay is suppressed on /files and /import, which own their own
 * drop zones — globals must never fight a page's native affordance.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import DocImportReview from './DocImportReview';
import { DOC_IMPORT_EXTENSIONS, OPEN_DOC_IMPORT_EVENT, isDocImportFile } from '../../lib/addDataConfig';

/** Routes with their own drop zones — the global overlay stays out of their way. */
const NATIVE_DROP_ROUTES = ['/files', '/import'];

export default function DocImportModal() {
  const [open, setOpen] = useState(false);
  const [initialFile, setInitialFile] = useState<File | null>(null);
  // Remounts DocImportReview per open so a finished run never leaks its
  // done-state into the next one (same pattern as GuidedTour's session key).
  const [session, setSession] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  // dragenter/dragleave fire for every child the cursor crosses — a depth
  // counter is the standard way to know when the drag truly left the window.
  const dragDepth = useRef(0);
  const location = useLocation();

  const openWith = useCallback((file: File | null) => {
    setInitialFile(file);
    setSession(s => s + 1);
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setInitialFile(null);
  }, []);

  // Entry 1 — the named event, from the Add-data menu or any other surface.
  useEffect(() => {
    const onOpen = (e: Event) => {
      const file = (e as CustomEvent<{ file?: File }>).detail?.file ?? null;
      openWith(file);
    };
    window.addEventListener(OPEN_DOC_IMPORT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_DOC_IMPORT_EVENT, onOpen);
  }, [openWith]);

  // Entry 2 — app-wide drag-and-drop.
  const overlayAllowed = !open && !NATIVE_DROP_ROUTES.some(r => location.pathname.startsWith(r));
  useEffect(() => {
    const hasFiles = (e: DragEvent) => !!e.dataTransfer && Array.from(e.dataTransfer.types).includes('Files');

    const onDragEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      dragDepth.current += 1;
      if (overlayAllowed) setDragActive(true);
    };
    const onDragLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragActive(false);
    };
    const onDragOver = (e: DragEvent) => {
      // preventDefault is required for the window to be a legal drop target —
      // and it stops the browser navigating away to the dropped file.
      if (hasFiles(e)) e.preventDefault();
    };
    const onDrop = (e: DragEvent) => {
      dragDepth.current = 0;
      setDragActive(false);
      if (!hasFiles(e)) return;
      e.preventDefault();
      if (!overlayAllowed) return; // a native drop zone (or the open modal) owns this drop
      const file = e.dataTransfer?.files?.[0];
      if (!file) return;
      if (!isDocImportFile(file.name)) {
        const exts = DOC_IMPORT_EXTENSIONS.map(x => `.${x}`).join(' / ');
        toast.error(`Drop a ${exts} document for Stories — spreadsheets import via "+ Add data" → Import a spreadsheet.`);
        return;
      }
      openWith(file);
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, [overlayAllowed, openWith]);

  return (
    <>
      {/* Full-screen drop invitation while a file is dragged over the app */}
      {dragActive && overlayAllowed && (
        <div className="fixed inset-0 z-[70] bg-brand-primary-dark/80 backdrop-blur-sm flex items-center justify-center pointer-events-none">
          <div className="border-2 border-dashed border-white/70 rounded-3xl px-16 py-14 text-center">
            <div className="text-6xl mb-4">📝</div>
            <div className="text-white text-2xl font-bold">Drop your meeting notes</div>
            <div className="text-teal-100 text-sm mt-2 font-medium">
              Avium reads the document and drafts the Stories — nothing is created until you approve.
            </div>
            <div className="text-teal-200/80 text-xs mt-4">
              {DOC_IMPORT_EXTENSIONS.map(e => `.${e}`).join(' · ')} · up to 20 MB
            </div>
          </div>
        </div>
      )}

      {/* The modal — DocImportReview hosted above whatever page the user is on */}
      {open && (
        <div className="fixed inset-0 z-[65] bg-black/50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
          <div className="bg-surface rounded-2xl shadow-2xl w-full max-w-3xl my-auto relative">
            <button
              onClick={close}
              aria-label="Close"
              className="absolute top-4 right-4 w-8 h-8 rounded-full text-ink-faint hover:text-ink-muted hover:bg-wash text-lg leading-none transition-colors"
            >
              ✕
            </button>
            <div className="p-6 sm:p-8">
              <DocImportReview key={`doc-import-${session}`} initialFile={initialFile} onRequestClose={close} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
